// Formik
import { useFormik } from "formik";

// Libraries
import { useTranslation } from "react-i18next";

// Material UI
import { Button, InputAdornment, Stack, Typography } from "@mui/material";
import { LockOutlined, MailOutline } from "@mui/icons-material";

// Validation
import { mbsSignInValidationSchema } from "Validation/UserValidation";

// Common
import { FormikTextField } from "Common/Components";

// Components
import MBSAuthFormHeader from "./MBSAuthFormHeader";

// Styles
import { styles } from "./styles";

const MBSLoginForm = ({ onSubmit, onForgotPassword, onSignInWithSso }) => {
  const { t } = useTranslation();
  const formik = useFormik({
    initialValues: { userEmail: "", password: "" },
    validationSchema: mbsSignInValidationSchema(t),
    onSubmit: async (values) => {
      await onSubmit(values.userEmail.trim(), values.password);
    },
  });

  return (
    <Stack
      component="form"
      id="mbs-login-form"
      noValidate
      onSubmit={formik.handleSubmit}
      spacing={3}
    >
      <MBSAuthFormHeader
        title={t("mbsAuth.emailSignInTitle")}
        description={t("mbsAuth.emailSignInDescription")}
      />
      <Stack spacing={2}>
        <FormikTextField
          formik={formik}
          name="userEmail"
          field={{
            type: "email",
            label: t("mbsAuth.userEmail"),
            required: false,
            autoComplete: "email",
            slotProps: {
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline />
                  </InputAdornment>
                ),
              },
            },
          }}
        />
        <FormikTextField
          formik={formik}
          name="password"
          field={{
            type: "password",
            label: t("mbsAuth.password"),
            required: false,
            autoComplete: "current-password",
            helperTextAction: (
              <Button
                type="button"
                variant="text"
                disableRipple
                onClick={onForgotPassword}
                sx={styles.forgotPasswordButton}
              >
                {t("mbsAuth.forgotPassword")}
              </Button>
            ),
            slotProps: {
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined />
                  </InputAdornment>
                ),
              },
            },
          }}
        />
      </Stack>
      <Stack spacing={2}>
        <Button
          type="submit"
          form="mbs-login-form"
          variant="contained"
          disabled={formik.isSubmitting}
          fullWidth
        >
          {formik.isSubmitting ? t("mbsAuth.signingIn") : t("mbsAuth.signIn")}
        </Button>
        <Typography variant="body2" textAlign="center">
          {t("mbsAuth.or")}
        </Typography>
        <Button variant="outlined" onClick={onSignInWithSso} fullWidth>
          {t("mbsAuth.useWorkEmailSso")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default MBSLoginForm;
